/**
 * 认证相关工具函数
 */

import type { Router, NavigationGuardNext } from 'vue-router';

import { useNavigation } from '@/composables/useNavigation'


/**
 * 判断是否是开发环境（基于域名是否是 localhost）
 */
export const isLocalDevelopment = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

/**
 * 从 URL 查询参数中获取 token
 * @param url - 完整的 URL 或查询字符串
 * @returns token 或 null
 */
export const getTokenFromUrl = (url?: string): string | null => {
  const searchParams = url
    ? new URLSearchParams(url.split('?')[1] || '')
    : new URLSearchParams(window.location.search);

  return searchParams.get('token');
};

/**
 * 从 cookie 中获取指定的值
 * @param name - cookie 的名称
 * @returns cookie 值或 null
 */
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

/**
 * 从 cookie 中获取 token
 * @param cookieName - cookie 的名称，默认为 'access_token'
 * @returns token 或 null
 */
export const getTokenFromCookie = (cookieName: string = 'access_token'): string | null => {
  return getCookie(cookieName);
};

export function getValidToken(): string {
  const cookieToken = getTokenFromCookie('access_token');
  if (cookieToken) {
    return cookieToken;
  }
  // 否则从localStorage获取
  return '';
}
/**
 * 删除指定的 cookie
 * @param name - cookie 的名称
 * @param path - cookie 的路径，默认为 '/'
 * @param domain - cookie 的域名（可选）
 * @param secure - 是否是安全 cookie（可选）
 * @param sameSite - SameSite 属性（可选）
 */
export const deleteCookie = () => {
  // 获取当前域名并提取父域
  const domainParts = window.location.hostname.split('.');
  let parentDomain = '';

  // 如果是 localhost 或 IP 地址，不设置 domain
  if (window.location.hostname === 'localhost' || /^(\d+\.){3}\d+$/.test(window.location.hostname)) {
    document.cookie = 'access_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=None';
  } else {
    // 对于生产环境，获取父域名
    if (domainParts.length >= 2) {
      // 取最后两部分作为父域名，例如：对于 test.linccareer.com，取 linccareer.com
      parentDomain = domainParts.slice(-2).join('.');
      document.cookie = `access_token=; Domain=.${parentDomain}; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=None`;
    } else {
      // 如果域名部分少于2个，直接使用当前域名
      document.cookie = 'access_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=None';
    }
  }
}
// window.deleteCookie = deleteCookie;

/**
 * 获取登录URL配置（支持外部URL）
 */
export const getLoginUrl = () => {
  const loginUrl = import.meta.env.VITE_LOGIN_URL || '/login';
  if (location.href.includes('localhost')) {
    return "http://localhost:5174/login"
  }
  return loginUrl;
};

/**
 * 获取当前页面的完整URL（包含域名）
 * @param path - 路径（如 /jobs/123 或完整URL）
 * @returns 完整的URL
 */
const getFullUrl = (path: string): string => {
  // 如果已经是完整URL，直接返回
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  // 构建完整URL：协议 + 域名 + 路径
  const { protocol, host } = window.location;
  // 确保路径以 / 开头
  const fullPath = path.startsWith('/') ? path : `/${path}`;
  return `${protocol}//${host}${fullPath}`;
};

/**
 * 跳转到登录页面（处理内部和外部URL）
 * @param next - 路由守卫的 next 函数（可选，如果提供则使用路由跳转）
 * @param router - Vue Router 实例（可选，如果提供则使用路由跳转）
 * @param redirectPath - 登录后要重定向的路径（可选）
 */
export const redirectToLogin = (
  options: {
    next?: NavigationGuardNext;
    router?: Router;
    delay?: number;
    redirectPath?: string;
  } = {}
) => {
  const { next, router, redirectPath, delay } = options;
  const loginUrl = getLoginUrl();

  // 判断是否是外部登录URL
  const isExternalLogin = loginUrl.startsWith('http://') || loginUrl.startsWith('https://');
  // 清空 cookie 中的 access_token（需要匹配设置时的属性）
  deleteCookie();
  // 如果是外部登录URL，使用 window.location.href 跳转
  // return;
  if (redirectPath) {
    // 外部登录URL时，redirect 必须是完整URL（包含域名）
    // const fullRedirectUrl = getFullUrl(redirectPath);
    // 所有的登录情况都跳转回首页
    const fullRedirectUrl = import.meta.env.VITE_CRAEER_URL;
    const separator = loginUrl.includes('?') ? '&' : '?';
    // console.log('separator', `${loginUrl}${separator}redirect=${encodeURIComponent(fullRedirectUrl)}`)
    if (delay) {
      setTimeout(() => {
        // window.location.href = `${loginUrl}${separator}redirect=${encodeURIComponent(fullRedirectUrl)}`;
        window.location.href = `${loginUrl}`;
      }, delay)
    } else {
      window.location.href = `${loginUrl}`;
    }
  } else {
    setTimeout(() => {
      window.location.href = loginUrl
    }, delay || 0)
  }

};
